import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import {
  MilestonePriority,
  MilestonePriorityValues,
  ProjectDeliveryMode,
  ProjectStageStatus,
  type ProjectCommentParticipantDto,
  type ProjectDto,
  type ProjectGovernanceDto,
} from '@paladin-monorepo/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { projectGovernanceApi } from '@/lib/api/project-governance';
import { useControlsMutation, useGovernanceMutation } from './useGovernanceMutation';

const DEFAULT_ACCEPTANCE = '__senior_user';

export function GovernanceMilestonesSection({
  project,
  governance,
  participants,
  nameOf,
}: {
  project: ProjectDto;
  governance: ProjectGovernanceDto;
  participants: ProjectCommentParticipantDto[];
  nameOf: (userId: string | null) => string;
}) {
  const { t } = useTranslation();
  const isPm = governance.permissions.isProjectManager && governance.state !== 'closed';
  const internal = governance.deliveryMode === ProjectDeliveryMode.Internal;
  const stageById = new Map(governance.stages.map((s) => [s.id, s]));
  const openStages = governance.stages.filter((s) => s.status !== ProjectStageStatus.Closed);
  const plannedStages = governance.stages.filter((s) => s.status === ProjectStageStatus.Planned);
  const members = participants.filter((p) => p.accountSide === 'inquirer');

  const deleteMutation = useGovernanceMutation(project.id, (milestoneId: string) =>
    projectGovernanceApi.deleteMilestone(project.id, milestoneId, governance.version),
  );
  const assignMutation = useGovernanceMutation(project.id, (args: { milestoneId: string; stageId: string }) =>
    projectGovernanceApi.assignMilestoneStage(project.id, args.milestoneId, {
      stageId: args.stageId,
      expectedVersion: governance.version,
    }),
  );
  const priorityMutation = useGovernanceMutation(
    project.id,
    (args: { milestoneId: string; priority: MilestonePriority }) =>
      projectGovernanceApi.setMilestonePriority(project.id, args.milestoneId, {
        priority: args.priority,
        expectedVersion: governance.version,
      }),
  );

  return (
    <div className="space-y-4">
      {project.milestones.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('workspace.governance.milestones.empty')}</p>
      ) : (
        <ul className="space-y-2 text-sm">
          {project.milestones.map((m) => {
            const stage = m.stageId ? stageById.get(m.stageId) : undefined;
            const editable = !stage || stage.status === ProjectStageStatus.Planned;
            return (
              <li key={m.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border/60 pb-2">
                <span className="font-medium">{m.title}</span>
                <span className="text-xs text-muted-foreground">
                  {stage ? stage.title : t('workspace.governance.milestones.noStage')}
                </span>
                {isPm && editable ? (
                  <Select
                    value={m.priority ?? MilestonePriority.Must}
                    onValueChange={(priority) =>
                      priorityMutation.mutate({ milestoneId: m.id, priority: priority as MilestonePriority })
                    }
                    disabled={priorityMutation.isPending}
                  >
                    <SelectTrigger className="h-8 w-36" aria-label={t('workspace.governance.milestones.priority')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MilestonePriorityValues.map((p) => (
                        <SelectItem key={p} value={p}>
                          {t(`workspace.governance.milestones.priorityValue.${p}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge variant="outline">
                    {t(`workspace.governance.milestones.priorityValue.${m.priority ?? MilestonePriority.Must}`)}
                  </Badge>
                )}
                {isPm && (
                  <DurationInline
                    projectId={project.id}
                    milestoneId={m.id}
                    durationWorkingDays={m.durationWorkingDays ?? null}
                  />
                )}
                {internal && (
                  <span className="text-xs text-muted-foreground">
                    {t('workspace.governance.milestones.teamManager')}: {nameOf(m.teamManagerUserId ?? null)}
                    {' · '}
                    {t('workspace.governance.milestones.acceptanceUser')}: {nameOf(m.acceptanceUserId ?? null)}
                  </span>
                )}
                {isPm && !m.stageId && openStages.length > 0 && (
                  <Select
                    onValueChange={(stageId) => assignMutation.mutate({ milestoneId: m.id, stageId })}
                    disabled={assignMutation.isPending}
                  >
                    <SelectTrigger className="h-8 w-48">
                      <SelectValue placeholder={t('workspace.governance.milestones.assignStage')} />
                    </SelectTrigger>
                    <SelectContent>
                      {openStages.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {isPm && internal && editable && m.stageId && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="ml-auto h-8 w-8"
                    aria-label={t('workspace.governance.milestones.delete')}
                    onClick={() => deleteMutation.mutate(m.id)}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {isPm && internal && plannedStages.length > 0 && (
        <CreateMilestoneForm
          projectId={project.id}
          governance={governance}
          stages={plannedStages}
          members={members}
        />
      )}
    </div>
  );
}

function CreateMilestoneForm({
  projectId,
  governance,
  stages,
  members,
}: {
  projectId: string;
  governance: ProjectGovernanceDto;
  stages: ProjectGovernanceDto['stages'];
  members: ProjectCommentParticipantDto[];
}) {
  const { t } = useTranslation();
  const schema = yup
    .object({
      stageId: yup.string().required(t('workspace.governance.milestones.stageRequired')),
      title: yup.string().trim().required(t('workspace.governance.milestones.titleRequired')).max(255),
      dueDate: yup.string().defined(),
      teamManagerUserId: yup.string().required(t('workspace.governance.milestones.teamManagerRequired')),
      acceptanceUserId: yup.string().required(),
      priority: yup.mixed<MilestonePriority>().oneOf(MilestonePriorityValues).required(),
      durationWorkingDays: yup
        .number()
        .transform((v, orig) => (orig === '' || orig == null ? undefined : v))
        .min(1)
        .optional(),
    })
    .required();

  const { control, register, handleSubmit, reset, formState } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      stageId: stages[0]?.id ?? '',
      title: '',
      dueDate: '',
      teamManagerUserId: '',
      acceptanceUserId: DEFAULT_ACCEPTANCE,
      priority: MilestonePriority.Must as MilestonePriority,
      durationWorkingDays: undefined,
    },
  });

  const mutation = useGovernanceMutation(
    projectId,
    (v: yup.InferType<typeof schema>) =>
      projectGovernanceApi.createMilestone(projectId, {
        expectedVersion: governance.version,
        stageId: v.stageId,
        title: v.title,
        dueDate: v.dueDate || null,
        teamManagerUserId: v.teamManagerUserId,
        acceptanceUserId: v.acceptanceUserId === DEFAULT_ACCEPTANCE ? null : v.acceptanceUserId,
        priority: v.priority,
        durationWorkingDays: v.durationWorkingDays ?? null,
      }),
    () => reset(),
  );

  const memberSelect = (name: 'teamManagerUserId' | 'acceptanceUserId', withDefault: boolean) => (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <Select value={field.value} onValueChange={field.onChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t('workspace.governance.milestones.selectMember')} />
          </SelectTrigger>
          <SelectContent>
            {withDefault && (
              <SelectItem value={DEFAULT_ACCEPTANCE}>{t('workspace.governance.milestones.defaultSeniorUser')}</SelectItem>
            )}
            {members.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.displayName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    />
  );

  return (
    <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-3 rounded-md border p-3">
      <h4 className="text-sm font-medium">{t('workspace.governance.milestones.createTitle')}</h4>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label>{t('workspace.governance.milestones.stage')}</Label>
          <Controller
            control={control}
            name="stageId"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {stages.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
        <div className="space-y-1">
          <Label>{t('workspace.governance.milestones.title')}</Label>
          <Input {...register('title')} />
          {formState.errors.title && <p className="text-xs text-destructive">{formState.errors.title.message}</p>}
        </div>
        <div className="space-y-1">
          <Label>{t('workspace.governance.milestones.dueDate')}</Label>
          <Input type="date" {...register('dueDate')} />
        </div>
        <div className="space-y-1">
          <Label>{t('workspace.governance.milestones.teamManager')}</Label>
          {memberSelect('teamManagerUserId', false)}
          {formState.errors.teamManagerUserId && (
            <p className="text-xs text-destructive">{formState.errors.teamManagerUserId.message}</p>
          )}
        </div>
        <div className="space-y-1">
          <Label>{t('workspace.governance.milestones.acceptanceUser')}</Label>
          {memberSelect('acceptanceUserId', true)}
        </div>
        <div className="space-y-1">
          <Label>{t('workspace.governance.milestones.priority')}</Label>
          <Controller
            control={control}
            name="priority"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MilestonePriorityValues.map((p) => (
                    <SelectItem key={p} value={p}>
                      {t(`workspace.governance.milestones.priorityValue.${p}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          <p className="text-xs text-muted-foreground">{t('workspace.governance.milestones.priorityHint')}</p>
        </div>
        <div className="space-y-1">
          <Label>{t('workspace.governance.milestones.duration')}</Label>
          <Input type="number" min={1} {...register('durationWorkingDays')} />
        </div>
      </div>
      <Button type="submit" size="sm" disabled={mutation.isPending}>
        {t('workspace.governance.milestones.create')}
      </Button>
    </form>
  );
}

function DurationInline({
  projectId,
  milestoneId,
  durationWorkingDays,
}: {
  projectId: string;
  milestoneId: string;
  durationWorkingDays: number | null;
}) {
  const { t } = useTranslation();
  const mutation = useControlsMutation(projectId, (next: number | null) =>
    projectGovernanceApi.setMilestoneDuration(projectId, milestoneId, { durationWorkingDays: next }),
  );
  return (
    <label className="flex items-center gap-1 text-xs text-muted-foreground">
      {t('workspace.governance.milestones.duration')}
      <Input
        type="number"
        min={1}
        className="h-8 w-20"
        defaultValue={durationWorkingDays ?? ''}
        disabled={mutation.isPending}
        onBlur={(e) => {
          const raw = e.target.value.trim();
          const next = raw === '' ? null : Number(raw);
          if (next !== null && (!Number.isFinite(next) || next < 1)) return;
          if (next === durationWorkingDays) return;
          mutation.mutate(next);
        }}
      />
    </label>
  );
}
